/**
 * The pure parts of the in-page tour: where a step's page is, when to navigate, where the card
 * goes and how far the page must scroll so the target is not hidden behind the card. No DOM here,
 * so everything is unit-tested (steps.test.ts).
 */

/** One stop of the tour, ready to show (the texts are already resolved). */
export interface PageTourStep {
  id: string;
  /** The page, relative to /o/:org ('' is the home, 'settings?tab=laser' may carry a query). */
  path: string;
  /** The value of a data-tour="…" attribute on a real element of that page; absent = centred card. */
  target?: string;
  title: string;
  body: string;
}

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

/** The address of a step's page. */
export function stepHref(orgId: string, path: string): string {
  return path ? `/o/${orgId}/${path}` : `/o/${orgId}`;
}

/**
 * Whether the router must move to `href`: another path, or a query value the step needs that the
 * current address lacks. Extra query values of the current address are left alone.
 */
export function needsNavigation(current: { pathname: string; search: string }, href: string): boolean {
  const url = new URL(href, 'http://tour.local');
  if (url.pathname.replace(/\/$/, '') !== current.pathname.replace(/\/$/, '')) return true;
  const now = new URLSearchParams(current.search);
  for (const [key, value] of url.searchParams) if (now.get(key) !== value) return true;
  return false;
}

/** The step after pressing an arrow key: in RTL the next page is to the left. null = not an arrow. */
export function arrowStep(key: string, rtl: boolean): 1 | -1 | null {
  if (key === 'ArrowLeft') return rtl ? 1 : -1;
  if (key === 'ArrowRight') return rtl ? -1 : 1;
  return null;
}

/** Keeps only what is inside the viewport (the part of a tall target that can be spotlit). */
export function clipToViewport(box: Box, viewport: Size): Box {
  const top = Math.max(0, box.top);
  const left = Math.max(0, box.left);
  const bottom = Math.min(viewport.height, box.top + box.height);
  const right = Math.min(viewport.width, box.left + box.width);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/** The spotlight is the target grown by `padding` on every side. */
export function spotlightBox(target: Box, padding = 6): Box {
  return {
    top: target.top - padding,
    left: target.left - padding,
    width: target.width + padding * 2,
    height: target.height + padding * 2,
  };
}

export type CardPlacement =
  | { mode: 'centered' }
  | { mode: 'sheet' }
  | { mode: 'floating'; top: number; left: number };

export interface PlaceOptions {
  /** The (clipped) spotlight, or null when the target is missing. */
  target: Box | null;
  card: Size;
  viewport: Size;
  /** Below the md breakpoint the card is a bottom sheet. */
  mobile: boolean;
  gap?: number;
  margin?: number;
}

/**
 * Where the card goes: a missing target centres it; a phone gets the bottom sheet; on a desktop it
 * goes below the target, else above, else beside it, else (a target as big as the page) low in the
 * viewport. Always fully inside the viewport.
 */
export function placeCard({ target, card, viewport, mobile, gap = 14, margin = 16 }: PlaceOptions): CardPlacement {
  if (!target || target.width <= 0 || target.height <= 0) return { mode: 'centered' };
  if (mobile) return { mode: 'sheet' };
  const clampX = (x: number) => Math.min(Math.max(margin, x), Math.max(margin, viewport.width - card.width - margin));
  const clampY = (y: number) => Math.min(Math.max(margin, y), Math.max(margin, viewport.height - card.height - margin));
  const centerX = clampX(target.left + target.width / 2 - card.width / 2);
  const centerY = clampY(target.top + target.height / 2 - card.height / 2);
  const below = target.top + target.height + gap;
  if (below + card.height + margin <= viewport.height) return { mode: 'floating', top: below, left: centerX };
  const above = target.top - gap - card.height;
  if (above >= margin) return { mode: 'floating', top: above, left: centerX };
  const right = target.left + target.width + gap;
  if (right + card.width + margin <= viewport.width) return { mode: 'floating', top: centerY, left: right };
  const left = target.left - gap - card.width;
  if (left >= margin) return { mode: 'floating', top: centerY, left };
  return { mode: 'floating', top: viewport.height - card.height - margin, left: centerX };
}

/**
 * How far the page scrolls (positive = down) so the target sits in the free part of the viewport:
 * below `margin` from the top and above the card (`reservedBottom`, the sheet on a phone). A target
 * taller than the free part is aligned to the top. 0 when it is already there.
 */
export function scrollDelta(target: Box, viewportHeight: number, reservedBottom: number, margin = 16): number {
  const free = viewportHeight - reservedBottom - margin * 2;
  if (target.height >= free) return Math.round(target.top - margin);
  const bottom = target.top + target.height;
  if (target.top < margin) return Math.round(target.top - margin);
  if (bottom > viewportHeight - reservedBottom - margin) return Math.round(bottom - (viewportHeight - reservedBottom - margin));
  return 0;
}
